import { useEffect, useRef } from 'react'
import { useRouter } from 'next/router'
import { usePagination } from 'react-instantsearch'
import omit from 'lodash/omit'

import { QueryParam } from '../../utils/constants'

/**
 * Slightly modified version of usePaginationPageData that uses instantSearch pagination
 * to store the current state
 */
const useInstantSearchPaginationPageData = () => {
  const { query, asPath, replace, push, isReady } = useRouter()

  const pageQuery = query[QueryParam.page]
  const [baseUrlWithHash] = asPath.split('?')
  const [baseUrl, urlHash] = baseUrlWithHash.split('#')
  const initializedRef = useRef(false)

  const { currentRefinement: page, refine: setCurrentPage } = usePagination()
  const currentPage = page + 1

  const removePageQueryFromUrl = () => {
    const currentQueries = omit(query, [
      QueryParam.filters,
      QueryParam.page,
      QueryParam.supplierCode,
    ])

    replace({
      pathname: baseUrl,
      query: currentQueries,
      hash: urlHash,
    },
    undefined,
    { shallow: true, scroll: true })
  }

  const updateUrl = (pageNumber: number) => {
    // Do not set the page query if navigating to the 1st page
    if (pageNumber === 1) {
      removePageQueryFromUrl()
      return
    }
    // Get the current URL path without any existing params, pass them later separately
    // to avoid collision
    push(
      {
        pathname: baseUrl,
        query: {
          ...omit(query, QueryParam.filters),
          page: pageNumber,
        },
      },
      undefined,
      { shallow: true, scroll: true },
    )
  }

  useEffect(() => {
    // After the router is ready, set the page query
    if (isReady) {
      const numberPageQuery = Number(pageQuery)
      if (!initializedRef.current) {
        // On initial page load, update page from url
        // After this the hook will control the state
        setCurrentPage(numberPageQuery - 1 || 0)
        initializedRef.current = true
      }
      if (pageQuery && (Number.isNaN(numberPageQuery) || numberPageQuery === 1)) {
        removePageQueryFromUrl()
      }
    }
  }, [pageQuery, isReady])

  const setPageNumber = (pageNumber: number) => {
    updateUrl(pageNumber)
    setCurrentPage(pageNumber - 1)
  }

  return {
    currentPage,
    setPageNumber,
  }
}

export default useInstantSearchPaginationPageData
